'use client'

import React, { useState, useEffect, useRef, FormEvent } from 'react'

import { usePathname } from 'next/navigation'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ChevronDown, Send, RotateCcw } from 'lucide-react'
import type { Message } from '../types'
import { Role } from '../types'
import { loadLocale } from '../locales/localLoader'
import NextImage from 'next/image'
import { chatWithAI } from '@/lib/actions/chat/chatWithAI'

// Extend Message type to include metadata
interface ExtendedMessage extends Message {
  isSessionLimitMessage?: boolean
}

const LoadingDots: React.FC = () => (
  <div className="flex space-x-1 p-1">
    <div
      className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
      style={{ animationDelay: '0s' }}
    ></div>
    <div
      className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
      style={{ animationDelay: '0.1s' }}
    ></div>
    <div
      className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
      style={{ animationDelay: '0.2s' }}
    ></div>
  </div>
)

// --- Helper component for rendering code blocks with a copy button ---
const CodeBlock: React.FC<{
  className?: string
  children?: React.ReactNode
}> = ({ className, children }) => {
  const [isCopied, setIsCopied] = useState(false)
  const language = className?.replace('language-', '') || 'code'

  const handleCopy = () => {
    if (children && typeof children === 'string') {
      navigator.clipboard.writeText(children)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    }
  }

  return (
    <div className="bg-slate-100 rounded-lg my-2 text-sm font-mono text-slate-800">
      <div className="flex items-center justify-between px-4 py-1.5 bg-slate-200/70 rounded-t-lg">
        <span className="text-slate-500 text-xs">{language}</span>
        <button
          onClick={handleCopy}
          className="text-slate-500 hover:text-slate-900 text-xs font-sans flex items-center gap-1.5 p-1 rounded-md hover:bg-slate-300/60 transition-colors cursor-pointer"
          aria-label="Copy code"
        >
          {isCopied ? (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              Copy
            </>
          )}
        </button>
      </div>
      <div className="overflow-x-auto p-4">
        <code className={className}>{children}</code>
      </div>
    </div>
  )
}

export const FloatingChatbot: React.FC = () => {
  const pathname = usePathname()
  const lang = pathname?.startsWith('/en') ? 'en' : 'es'
  const locale = loadLocale(lang)
  const chatbotTexts = locale?.components?.chatbot || {}
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<ExtendedMessage[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  
  // Limits to prevent abuse
  const MAX_MESSAGE_LENGTH = 250 // Maximum characters per message
  const MAX_MESSAGES_PER_SESSION = 20 // Maximum messages per chat session

  const chatHistoryRef = useRef<HTMLDivElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const BRAND_COLOR = '#00A99D'
  
  // LocalStorage keys for persistence
  const STORAGE_KEY = 'redi-chatbot-conversation'
  const CHAT_STATE_KEY = 'redi-chatbot-state'
  
  // Helper functions for localStorage
  const saveConversationToStorage = (messages: ExtendedMessage[]) => {
    try {
      if (typeof window !== 'undefined') {
        // Filter out empty messages (loading bubbles) before saving
        const validMessages = messages.filter(msg => msg.content.trim() !== '')
        
        const conversationData = {
          messages: validMessages,
          timestamp: Date.now(),
          language: lang
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(conversationData))
      }
    } catch (error) {
      console.warn('Failed to save conversation to localStorage:', error)
    }
  }

  const loadConversationFromStorage = (): ExtendedMessage[] | null => {
    try {
      if (typeof window !== 'undefined') {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
          const conversationData = JSON.parse(stored)
          // Check if the stored conversation is from the same language and not too old (24 hours)
          const isCurrentLanguage = conversationData.language === lang
          const isRecent = Date.now() - conversationData.timestamp < 24 * 60 * 60 * 1000
          
          if (isCurrentLanguage && isRecent && conversationData.messages) {
            // Filter out any empty messages that might have been saved
            const validMessages = conversationData.messages.filter((msg: ExtendedMessage) => msg.content.trim() !== '')
            return validMessages.length > 0 ? validMessages : null
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load conversation from localStorage:', error)
    }
    return null
  }

  const clearConversationFromStorage = () => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(STORAGE_KEY)
      }
    } catch (error) {
      console.warn('Failed to clear conversation from localStorage:', error)
    }
  }

  // Helper functions for chat state persistence
  const saveChatStateToStorage = (isOpen: boolean) => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(CHAT_STATE_KEY, JSON.stringify({ isOpen }))
      }
    } catch (error) {
      console.warn('Failed to save chat state to localStorage:', error)
    }
  }

  const loadChatStateFromStorage = (): boolean => {
    try {
      if (typeof window !== 'undefined') {
        const stored = localStorage.getItem(CHAT_STATE_KEY)
        if (stored) {
          const chatState = JSON.parse(stored)
          return chatState.isOpen || false
        }
      }
    } catch (error) {
      console.warn('Failed to load chat state from localStorage:', error)
    }
    return false
  }

  // Helper function to check if last user message needs a response
  const getLastUnansweredUserMessage = (messages: ExtendedMessage[]): string | null => {
    if (messages.length === 0) return null
    
    // Check if the last message is from user without a following model response
    const lastMessage = messages[messages.length - 1]
    if (lastMessage.role === Role.USER) {
      return lastMessage.content
    }
    
    // Check if there are at least 2 messages and the second to last is user
    // while the last is an empty model message (interrupted response)
    if (messages.length >= 2) {
      const secondToLast = messages[messages.length - 2]
      if (secondToLast.role === Role.USER && lastMessage.role === Role.MODEL && lastMessage.content.trim() === '') {
        return secondToLast.content
      }
    }
    
    return null
  }
  
  // Check if session limit has been reached
  const sessionLimitReached = messages.filter(m => m.role === Role.USER).length >= MAX_MESSAGES_PER_SESSION

  const getWelcomeMessage = (): ExtendedMessage => ({
    role: Role.MODEL,
    content: chatbotTexts.welcome_message || "Hi! I'm your Redi eSIM assistant"
  })

  const initializeChat = () => {
    const storedMessages = loadConversationFromStorage()
    if (storedMessages && storedMessages.length > 0) {
      setMessages(storedMessages)
    } else {
      setMessages([getWelcomeMessage()])
    }
  }

  // Load conversation and chat state from localStorage on component mount (client-side only)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Load chat state
      const storedIsOpen = loadChatStateFromStorage()
      setIsOpen(storedIsOpen)
      
      // Load conversation if no messages are loaded yet
      if (messages.length === 0) {
        const storedMessages = loadConversationFromStorage()
        if (storedMessages && storedMessages.length > 0) {
          setMessages(storedMessages)
          
          // Check if last message needs a response and auto-retry
          const unansweredMessage = getLastUnansweredUserMessage(storedMessages)
          if (unansweredMessage) {
            // Add a small delay to ensure UI is ready
            setTimeout(() => {
              sendMessage(unansweredMessage, true) // true indicates this is a retry
            }, 500)
          }
        }
      }
    }
  }, [])

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      initializeChat()
    }
  }, [isOpen])

  // Update welcome message when language changes
  useEffect(() => {
    if (isOpen && messages.length === 1 && messages[0].role === Role.MODEL) {
      setMessages([getWelcomeMessage()])
    }
  }, [lang])

  useEffect(() => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight
    }
  }, [messages])

  // Save conversation to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      saveConversationToStorage(messages)
    }
  }, [messages, lang])

  // Save chat state to localStorage whenever isOpen changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      saveChatStateToStorage(isOpen)
    }
  }, [isOpen])

  const handleRestart = () => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setIsLoading(false)
    setInput('')
    clearConversationFromStorage() // Clear stored conversation
    setMessages([]) // Clear all messages first
    setMessages([getWelcomeMessage()]) // Start fresh with welcome message
  }

  const sendMessage = async (messageText: string, isRetry: boolean = false) => {
    const trimmedMessage = messageText.trim()
    if (!trimmedMessage) return
    
    // Check message length limit
    if (trimmedMessage.length > MAX_MESSAGE_LENGTH) {
      alert(lang === 'es' 
        ? `El mensaje es demasiado largo. Máximo ${MAX_MESSAGE_LENGTH} caracteres.`
        : `Message is too long. Maximum ${MAX_MESSAGE_LENGTH} characters.`)
      return
    }
    
    // Check session message limit
    const userMessagesCount = messages.filter(m => m.role === Role.USER).length
    if (userMessagesCount >= MAX_MESSAGES_PER_SESSION) {
      // Add goodbye message
      const goodbyeMessage: ExtendedMessage = {
        role: Role.MODEL,
        content: chatbotTexts.session_limit_reached || "Thank you for chatting with me! 😊 You've reached the message limit for this session. If you need more help, you can start a new conversation.",
        isSessionLimitMessage: true
      }
      setMessages((prev) => [...prev, goodbyeMessage])
      setIsLoading(false)
      return
    }

    // Only add user message if it's not a retry (to avoid duplicates)
    if (!isRetry) {
      const userMessage: Message = { role: Role.USER, content: trimmedMessage }
      setMessages((prev) => [...prev, userMessage])
    }
    setIsLoading(true)

    const predefinedAnswer = chatbotTexts.predefined_answers?.[trimmedMessage]

    if (predefinedAnswer) {
      // Add thinking bubble for predefined answers
      setMessages((prev) => [...prev, { role: Role.MODEL, content: '' }])
      
      // Simulate thinking for a better UX with predefined answers
      setTimeout(() => {
        setMessages((prev) => {
          const newMessages = [...prev]
          newMessages[newMessages.length - 1].content = predefinedAnswer
          return newMessages
        })
        setIsLoading(false)
      }, 500)
      return
    }

    // If not a predefined answer, use the API
    // Add the "thinking" bubble for API calls (or ensure it exists for retries)
    setMessages((prev) => {
      // For retries, check if we already have an empty thinking bubble
      if (isRetry && prev.length > 0 && prev[prev.length - 1].role === Role.MODEL && prev[prev.length - 1].content === '') {
        return prev // Already has thinking bubble
      }
      return [...prev, { role: Role.MODEL, content: '' }]
    })

    // Create new AbortController for this request
    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      const stream = await chatWithAI({
        messages: [
          ...messages.map((msg) => ({
            role: msg.role === Role.USER ? 'user' : 'assistant',
            content: msg.content
          } as const)),
          { role: 'user' as const, content: trimmedMessage }
        ],
        locale: lang
      })

      const reader = stream.getReader()
      const decoder = new TextDecoder()
      let text = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        text += chunk
      }
      
      // Update message only after receiving complete response
      setMessages((prev) => {
        const newMessages = [...prev]
        newMessages[newMessages.length - 1].content = text
        return newMessages
      })
    } catch (error: any) {
      // Don't show error if request was aborted
      if (error.name === 'AbortError') {
        return
      }

      console.error('Error sending message:', error)
      
      // Handle session limit error specifically
      if (error.message && error.message.includes(MAX_MESSAGES_PER_SESSION.toString())) {
        // Show goodbye message for session limit
        setMessages((prev) => {
          const newMessages = [...prev]
          const lastMessage = newMessages[newMessages.length - 1]
          lastMessage.content = chatbotTexts.session_limit_reached || "Thank you for chatting with me! 😊 You've reached the message limit for this session. If you need more help, you can start a new conversation."
          lastMessage.isSessionLimitMessage = true
          return newMessages
        })
        setIsLoading(false)
        return
      }
      
      const errorMessage: Message = {
        role: Role.MODEL,
        content: '¡Uy! Algo salió mal. Por favor, inténtalo de nuevo.'
      }
      setMessages((prev) => {
        const newMessages = [...prev]
        newMessages[newMessages.length - 1] = errorMessage
        return newMessages
      })
    } finally {
      setIsLoading(false)
      // Clear abort controller reference
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null
      }
    }
  }

  const handleSend = async (e: FormEvent) => {
    e.preventDefault()
    const currentInput = input.trim()
    if (isLoading || !currentInput) return

    setInput('')
    await sendMessage(currentInput)
  }

  const handlePredefinedClick = (command: string) => {
    if (isLoading) return
    setInput('')
    sendMessage(command)
  }

  const markdownComponents = {
    p: ({ ...props }) => <p className="mb-2 last:mb-0" {...props} />,
    ul: ({ ...props }) => (
      <ul className="list-disc list-inside space-y-1 my-2 pl-2" {...props} />
    ),
    ol: ({ ...props }) => (
      <ol className="list-decimal list-inside space-y-1 my-2 pl-2" {...props} />
    ),
    a: ({ ...props }) => (
      <a
        style={{ color: BRAND_COLOR }}
        className="underline hover:opacity-80"
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      />
    ),
    blockquote: ({ ...props }) => (
      <blockquote
        className="border-l-4 border-slate-300 pl-4 italic my-2 text-slate-500"
        {...props}
      />
    ),
    code: ({ node, inline, className, children, ...props }: any) => {
      const match = /language-(\w+)/.exec(className || '')
      return !inline && match ? (
        <CodeBlock className={className}>
          {String(children).replace(/\n$/, '')}
        </CodeBlock>
      ) : (
        <code
          style={{ color: BRAND_COLOR, fontWeight: 'bold' }}
          className="bg-slate-200/80 font-mono rounded-sm px-1.5 py-0.5 text-[0.9em] mx-0.5"
          {...props}
        >
          {children}
        </code>
      )
    },
    strong: ({ ...props }) => (
      <strong style={{ color: BRAND_COLOR }} {...props} />
    ),
    table: ({ ...props }) => (
      <div className="overflow-x-auto">
        <table
          className="table-auto w-full my-2 border-collapse border border-slate-300"
          {...props}
        />
      </div>
    ),
    thead: ({ ...props }) => <thead className="bg-slate-100" {...props} />,
    th: ({ ...props }) => (
      <th
        className="border border-slate-300 px-3 py-1.5 text-left"
        {...props}
      />
    ),
    td: ({ ...props }) => (
      <td className="border border-slate-300 px-3 py-1.5" {...props} />
    )
  }

  return (
    <>
      <style>{`:root { --brand-color: ${BRAND_COLOR}; }`}</style>
      <div
        className={`fixed z-50 bottom-4 right-4 sm:bottom-8 sm:right-8 transition-all duration-300 ease-in-out ${isOpen ? 'opacity-0 scale-90 pointer-events-none' : 'opacity-100 scale-100'}`}
      >
        <button
          onClick={() => setIsOpen(true)}
          className={`bg-[var(--brand-color)] text-white h-10 sm:h-14  sm:pr-4
            sm:h-16 rounded-2xl sm:rounded-3xl shadow-2xl flex items-center justify-center hover:scale-102
             transform transition-transform duration-100 focus:outline-none focus:ring-4 focus:ring-[var(--brand-color)]/50 cursor-pointer`}
          aria-label={chatbotTexts.button_text}
        >
          <NextImage
            className="max-h-15 min-h-15 min-w-[14.04] sm:max-h-25 sm:min-w-[14.04] w-auto mb-3"
            src="/avatarexpert.webp"
            alt="avatarexpert"
            height={125}
            width={117}
            quality={80}
          />
          <div className="hidden sm:block ml-[-8px] text-left font-roboto font-bold">
            <div className="text-base sm:text-lg leading-tight">
              {chatbotTexts.talk_with || 'TALK WITH'}
            </div>
            <div className="text-base sm:text-lg leading-tight">
              {chatbotTexts.an_expert || 'AN EXPERT'}
            </div>
          </div>
        </button>
      </div>

      <div
        className={`fixed z-9999 inset-0 sm:bottom-8 sm:right-8 sm:inset-auto sm:w-[400px] sm:h-auto sm:max-h-[calc(100vh-6rem)] bg-white/90 backdrop-blur-lg border border-slate-200 rounded-none sm:rounded-xl shadow-2xl flex flex-col transform font-sans transition-all duration-300 ease-in-out
                ${isOpen ? 'opacity-100 translate-y-0 sm:translate-y-0' : 'opacity-0 translate-y-full sm:translate-y-16 pointer-events-none'}`}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-xl font-bold text-slate-800">
            {chatbotTexts.title}
          </h2>
          <div className="flex items-center gap-1">
            {messages.length > 1 && (
              <button
                onClick={handleRestart}
                className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded-full transition-colors cursor-pointer"
                aria-label={chatbotTexts.restart_chat}
              >
                <RotateCcw className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded-full transition-colors cursor-pointer"
              aria-label={chatbotTexts.minimize_chat}
            >
              <ChevronDown className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div
          ref={chatHistoryRef}
          className="flex-grow p-4 overflow-y-auto space-y-4"
        >
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex gap-3 w-full ${msg.role === Role.USER ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === Role.MODEL && (
                <div
                  style={{ backgroundColor: BRAND_COLOR }}
                  className="pt-[6px] w-8 h-8 rounded-full flex-shrink-0 mt-1 flex items-center justify-center overflow-hidden"
                >
                   <NextImage
              className="text-white/90 max-h-7.5 w-auto"
              src="/avatarexpert.webp"
              alt="avatarexpert"
              height={125}
              width={117}
              quality={80}
              />
                </div>
              )}
              <div
                className={`px-4 py-2.5 rounded-2xl max-w-[85%] text-left
                                ${
                                  msg.role === Role.USER
                                    ? 'bg-[var(--brand-color)] text-white rounded-br-lg whitespace-pre-wrap'
                                    : 'bg-slate-100 text-slate-800 rounded-bl-lg'
                                }`}
              >
                {msg.content === '' && msg.role === Role.MODEL && isLoading ? (
                  <LoadingDots />
                ) : msg.role === Role.MODEL ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents}
                  >
                    {msg.content}
                  </ReactMarkdown>
                ) : (
                  msg.content
                )}
              </div>
            </div>
          ))}
          
          {/* Show restart button after session limit message */}
          {messages.length > 0 && 
           messages[messages.length - 1]?.isSessionLimitMessage && (
            <div className="flex justify-center mt-4">
              <button
                onClick={handleRestart}
                className="cursor-pointer bg-[var(--brand-color)] text-white px-6 py-2 rounded-full hover:opacity-90 transition-opacity flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                {chatbotTexts.start_new_conversation || 'Start new conversation'}
              </button>
            </div>
          )}
          
          {messages.length === 1 && !isLoading && (
            <div className="border-t border-slate-200 pt-4 mt-4">
              <p className="text-sm text-slate-500 mb-3 text-center">
                {chatbotTexts.choose_option}
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {Object.values(chatbotTexts.conversation_starters || {}).map(
                  (command: any) => (
                    <button
                      key={command}
                      onClick={() => handlePredefinedClick(command)}
                      disabled={isLoading}
                      className="bg-white text-slate-700 border border-slate-300 text-sm py-1.5 px-3 rounded-full hover:bg-slate-100 transition-colors disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                    >
                      {command}
                    </button>
                  )
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 flex-shrink-0">
          <form onSubmit={handleSend} className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
                placeholder={chatbotTexts.placeholder}
                disabled={isLoading || sessionLimitReached}
                maxLength={MAX_MESSAGE_LENGTH}
                className="w-full bg-white text-slate-800 placeholder-slate-500 border border-slate-300 rounded-full py-2 px-4 pr-16 focus:outline-none focus:ring-2 focus:ring-[var(--brand-color)] transition-shadow disabled:opacity-50"
              />
              {input.length > 0 && (
                <span className={`absolute right-4 top-1/2 transform -translate-y-1/2 text-xs ${input.length >= MAX_MESSAGE_LENGTH ? 'text-red-500' : 'text-slate-400'}`}>
                  {input.length}/{MAX_MESSAGE_LENGTH}
                </span>
              )}
            </div>
            <button
              type="submit"
              disabled={isLoading || !input.trim() || sessionLimitReached}
              className="w-10 h-10 flex-shrink-0 bg-[var(--brand-color)] text-white rounded-full flex items-center justify-center transition-colors duration-200 hover:opacity-90 disabled:bg-slate-400 disabled:cursor-not-allowed cursor-pointer"
              aria-label={chatbotTexts.send}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </form>
        </div>
      </div>
    </>
  )
}
